import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Image, MessageSquare, Minimize2 } from 'lucide-react';
import { User, Message } from '../../types';
import { api } from '../../services/api';
import styles from './Chat.module.css';

interface ChatDrawerProps {
  currentUser: User | null;
  isOpen: boolean;
  onClose: () => void;
}

export const ChatDrawer: React.FC<ChatDrawerProps> = ({ currentUser, isOpen, onClose }) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'msg_welcome',
      conversation_id: 'conv_demo',
      sender: {
        id: 'usr_sarah_design',
        username: 'sarah_design',
        email: '',
        display_name: 'Sarah Connor',
        bio: '',
        avatar_url: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400&auto=format&fit=crop&q=80',
        banner_url: '',
        custom_theme: 'emerald-aurora',
        pronouns: 'she/her',
        location: '',
        website: '',
        social_links: {},
        verified: true,
        status: 'online',
        custom_status_text: '',
        is_email_verified: true,
        two_factor_enabled: false,
        created_at: Date.now(),
      },
      content: 'Hey! Welcome to Cordle real-time WebSocket messaging! 💬⚡',
      media_url: null,
      reply_to_id: null,
      reactions: {},
      is_edited: false,
      created_at: Date.now() - 60000,
    },
  ]);
  const [text, setText] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (!isOpen || !currentUser) return null;

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;

    const newMsg: Message = {
      id: `msg_${Date.now()}`,
      conversation_id: 'conv_demo',
      sender: currentUser,
      content: text.trim(),
      media_url: null,
      reply_to_id: null,
      reactions: {},
      is_edited: false,
      created_at: Date.now(),
    };

    setMessages((prev) => [...prev, newMsg]);
    setText('');
  };

  return (
    <div className={styles.drawer}>
      <div className={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <MessageSquare size={18} color="var(--accent-primary)" />
          <span className={styles.title}>Direct Message Chat</span>
        </div>
        <button onClick={onClose} style={{ color: 'var(--text-muted)' }}>
          <X size={18} />
        </button>
      </div>

      <div className={styles.messagesList}>
        {messages.map((msg) => {
          const isMe = msg.sender.id === currentUser.id;
          return (
            <div
              key={msg.id}
              className={`${styles.bubble} ${isMe ? styles.sent : styles.received}`}
            >
              {!isMe && <div style={{ fontSize: '0.75rem', fontWeight: '700', marginBottom: '2px', opacity: 0.8 }}>{msg.sender.display_name}</div>}
              <div>{msg.content}</div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      <form className={styles.inputArea} onSubmit={handleSend}>
        <input
          type="text"
          className={styles.input}
          placeholder="Type a message..."
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <button type="submit" className={styles.sendBtn}>
          <Send size={16} />
        </button>
      </form>
    </div>
  );
};
